import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Input, Button, Flex, Alert, theme } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { ScreenshotUploader } from './ScreenshotUploader';
import { redactCredentials } from '@prerender/prerender-shared/FE';
import { useSelector } from '../../../../../../hooks/usePrerenderUser';
import { SendMessagePayload } from './types';

const { TextArea } = Input;

interface MessageComposerProps {
  onSendMessage: (payload: SendMessagePayload) => void;
  disabled?: boolean;
  /**
   * Text the composer opens with. The user reads it and can edit or delete it;
   * nothing is ever sent on their behalf. Once the user has typed, a later
   * prefill is ignored — overwriting what somebody is writing loses their work.
   */
  prefillText?: string;
}

/**
 * Message composer with text input and screenshot upload
 */
export function MessageComposer({ onSendMessage, disabled = false, prefillText }: MessageComposerProps) {
  const { token } = theme.useToken();
  // The account's own Prerender API token. It is 20 mixed-case alphanumeric
  // characters, which is also the shape of a great many symbol names, so no
  // pattern can claim it — but the browser is holding the exact value, and an
  // exact match has no false positives at all (PRE-3499).
  const prerenderToken = useSelector((state) => state.prerenderUser?.token);
  const [text, setText] = useState(prefillText ?? '');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [redacted, setRedacted] = useState(false);
  const isUserEdited = useRef(false);

  useEffect(() => {
    if (prefillText === undefined || isUserEdited.current) {
      return;
    }

    setText(prefillText);
  }, [prefillText]);

  const canSend = Boolean(text.trim() || screenshot);

  const handleSend = () => {
    if (!canSend) {
      return;
    }

    // A user stuck at the token step pastes their Cloudflare token in to ask why
    // it failed (PRE-3499). Nothing that protects the stored credential reaches a
    // chat thread, so the token is removed before the message leaves the browser.
    // rest-api redacts again at its own boundary; this half exists to tell the
    // user, which the server cannot do once the message is already sent.
    //
    // Redacted, not refused: a refusal loses the question and the user retypes
    // the token to get past it.
    const safeText = redactCredentials(text.trim(), prerenderToken ? [prerenderToken] : []);
    setRedacted(safeText !== text.trim());

    onSendMessage({
      text: safeText,
      screenshot: screenshot || undefined,
    });

    setText('');
    setScreenshot(null);
    // A sent message ends the prefill's claim on the box: the next thing the
    // user types is theirs.
    isUserEdited.current = true;
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        padding: token.padding,
        borderTop: `1px solid ${token.colorBorder}`,
        backgroundColor: token.colorBgContainer,
      }}
    >
      <Flex vertical gap={token.sizeXS}>
        {redacted && (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setRedacted(false)}
            message="We removed something that looked like an API token. Nexus does not need your token to help you."
          />
        )}

        <ScreenshotUploader onFileSelect={setScreenshot} selectedFile={screenshot} disabled={disabled} />

        <Flex gap={token.sizeXS} align="flex-end">
          <TextArea
            value={text}
            onChange={(e) => {
              isUserEdited.current = true;
              setText(e.target.value);
            }}
            onKeyDown={handleKeyPress}
            placeholder="Message Nexus... (Enter to send, Shift+Enter for new line)"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={disabled}
            style={{
              flex: 1,
              resize: 'none',
            }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={disabled || !canSend}
            style={{
              height: 'auto',
              minHeight: 32,
            }}
          >
            Send
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
